import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { LayoutBand, Grid, Cell, H2, Paragraph, Separator, useContainerWidth, WhiteTheme } from '@fs/zion-ui'
import AuthProviderButtons from '@fs/zion-auth-provider-buttons'
import preserveFamilyStoriesImage from '../assets/PreserveFamilyStories.webp'

const backgroundImageCss = css`
  position: relative;
  background-size: cover;
  background-position: center;

  &::before {
    content: '';
    display: block;
    inset: 0;
    position: absolute;
    /* Dark overlay using linear-gradient */
    background: var(--background-gradient, rgba(0, 3, 7, 0.67));
    z-index: 0;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`
export default function PreserveFamilyStoriesSection(): JSX.Element {
  const { t } = useTranslation()
  const atWidth = useContainerWidth()

  return (
    <LayoutBand
      css={backgroundImageCss}
      image={preserveFamilyStoriesImage}
      maxWidth="xxl"
      marginY="xxl"
      marginX={atWidth({ default: 'sm', lg: 'xxl' })}
      minHeight={atWidth({ default: 500, lg: 600 })}
      style={{
        '--background-gradient': atWidth({
          default: null,
          lg: 'linear-gradient(110deg, rgba(0, 0, 0, 0.9) 30%,rgba(0, 0, 0, 0) 80%)',
        }),
      }}
    >
      <WhiteTheme>
        <Grid gutters={atWidth({ default: 'md', lg: 'xl' })}>
          <Cell columns={atWidth({ default: 12, lg: 10, xl: 8 })} alignY="middle">
            {/* Main heading */}
            <H2>{t('tree-overview.family-interview.preserve.heading', 'Preserve Your Family Stories Forever')}</H2>

            <Separator size="md" />

            {/* Description paragraph */}
            <Paragraph size="sm">
              {t(
                'tree-overview.family-interview.preserve.description',
                'Upload your family stories, photos, and recordings to FamilySearch Memories - a free, permanent archive that will preserve them for future generations.'
              )}
            </Paragraph>

            <Separator size="md" />

            {/* Bulleted list of content types */}
            <ul>
              <li>{t('tree-overview.family-interview.preserve.list-item1', 'Written stories and memories')}</li>
              <li>{t('tree-overview.family-interview.preserve.list-item2', 'Audio recordings')}</li>
              <li>{t('tree-overview.family-interview.preserve.list-item3', 'Photos and images')}</li>
              <li>{t('tree-overview.family-interview.preserve.list-item4', 'Scanned documents and certificates')}</li>
            </ul>

            <Separator size="lg" />

            {/* Authentication provider buttons */}
            <AuthProviderButtons
              size="sm"
              label={t('tree-overview.family-interview.preserve.cta-heading', 'Ask Your Family Now')}
              linkNamePrefix="PreserveFamilyStoriesSection"
              stacked
            />
          </Cell>
        </Grid>
      </WhiteTheme>
    </LayoutBand>
  )
}
